import MOBILE from "./mobileConfig";

/**
 * Our own face for every native date field, on phones only.
 *
 * WHY. On a real mobile browser `<input type="date">` is drawn by the
 * platform, and it draws it badly for this app: an EMPTY field shows nothing at
 * all (no "dd/mm/yyyy" — that is a desktop-Chrome nicety), and Android paints a
 * dropdown chevron beside whatever calendar icon the form already draws. CSS
 * alone could not fix it, because the chevron and the empty text belong to the
 * platform's painting — and every CSS attempt depended on exactly how Android
 * paints, which cannot be reproduced off a device.
 *
 * WHAT. A face is laid OVER each date input, inside its border: an opaque box in
 * the input's own background colour, reading "Select date" or the picked date as
 * "17 Sep 2026" (DD MMM YYYY), with a calendar icon. It covers the platform's text and
 * chevron, and leaves the input's own border (and its focus colour) showing
 * around it.
 *
 * WHAT IT DOES NOT DO — the whole point. The native input is still the control.
 * The face is `pointer-events: none`, so a tap lands on the input underneath and
 * opens the platform's own picker; the value, `min`/`max`, `disabled`, and every
 * form's onChange are untouched. Nothing here writes a value. `aria-hidden`
 * keeps the face out of the accessibility tree, where the input still speaks
 * for itself.
 *
 * HOW IT STAYS HONEST OUTSIDE REACT. It adds one element per input, appended to
 * the input's parent, and React tolerates that: React only ever inserts before,
 * and removes, nodes it owns. It keeps itself in sync from three signals:
 *
 *   · a MutationObserver on <body> — inputs arriving and leaving, and the
 *     `value` attribute, which React 16 syncs on controlled inputs, so a value a
 *     form SETS (seeding, clearing) is seen as well as one a person picks;
 *   · `input`/`change` events — a pick shows at once;
 *   · a ResizeObserver per parent — the face moves when the field does.
 *
 * THE PARENT IS MADE THE CONTAINING BLOCK. The face is `position: absolute` and
 * its coordinates are the input's `offsetLeft/Top`. Those only agree if both
 * measure from the same box, and only stay agreed while scrolling if that box
 * is INSIDE any scroll container — so a static parent is given
 * `position: relative` (and given it back when the layer leaves). A transformed
 * ancestor, like a sliding sheet, would otherwise have become the face's
 * containing block while `offsetParent` ignored it.
 *
 * Started and stopped by useIsMobile with the root class, so it runs exactly
 * when the mobile view does, portalled dialogs included. Off with
 * MOBILE.dateLayer.
 */

const SELECTOR = 'input[type="date"]';
const FACE = "zym-datefield";

/* labIcons' CalendarIcon, the path verbatim — the app's glyph, not a second one. */
const CALENDAR_SVG =
  '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M13.333 2.00002H12.6663V0.666687H11.333V2.00002H4.66634V0.666687H3.33301V2.00002H2.66634C1.93301 2.00002 1.33301 2.60002 1.33301 3.33335V14C1.33301 14.7334 1.93301 15.3334 2.66634 15.3334H13.333C14.0663 15.3334 14.6663 14.7334 14.6663 14V3.33335C14.6663 2.60002 14.0663 2.00002 13.333 2.00002ZM13.333 14H2.66634V6.66669H13.333V14ZM13.333 5.33335H2.66634V3.33335H13.333V5.33335Z"/></svg>';

const EMPTY_TEXT = "Select date";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * "2026-09-17" -> "17 Sep 2026" — DD MMM YYYY, exactly (product, 2026-09-17).
 *
 * NOT toLocaleDateString. Its short month is whatever the device's ICU data
 * says, and en-GB currently says "Sept" — four letters, and not the same on
 * every phone. A fixed three-letter table gives the same field on every device.
 *
 * Read from the value's PARTS, never `new Date("2026-09-17")`: that parses as
 * midnight UTC and names the day before anywhere west of Greenwich. The parts
 * are range-checked instead of round-tripped through a Date, so "2026-02-31"
 * shows nothing rather than quietly becoming 3 March.
 */
export const formatDateValue = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  if (!m) return "";
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return "";
  return `${m[3]} ${MONTHS[month - 1]} ${m[1]}`;
};

/** input -> { face, text, host, ro, last } */
const records = new Map();
/** host -> the inline `position` it had before the layer touched it */
const repositioned = new Map();

let running = false;
let bodyObserver = null;
let frame = 0;

const px = (v) => parseFloat(v) || 0;

const isFace = (node) =>
  !!node && node.nodeType === 1 && !!node.closest && !!node.closest(`.${FACE}`);

const release = (host) => {
  if (!repositioned.has(host)) return;
  for (const rec of records.values()) if (rec.host === host) return;
  host.style.position = repositioned.get(host);
  repositioned.delete(host);
};

const drop = (input) => {
  const rec = records.get(input);
  if (!rec) return;
  records.delete(input);
  if (rec.ro) rec.ro.disconnect();
  if (rec.face.parentNode) rec.face.parentNode.removeChild(rec.face);
  release(rec.host);
};

const sync = (input, rec) => {
  if (!input.isConnected || input.parentNode !== rec.host) {
    drop(input);
    return;
  }

  const visible = input.offsetWidth > 0 && input.offsetHeight > 0;
  const cs = window.getComputedStyle(input);
  const bl = px(cs.borderLeftWidth);
  const br = px(cs.borderRightWidth);
  const bt = px(cs.borderTopWidth);
  const bb = px(cs.borderBottomWidth);
  const bg = cs.backgroundColor;
  const shown = formatDateValue(input.value);

  /* Written only when something changed: the body observer watches `class`,
     and a face rewriting itself every frame would keep it awake for nothing. */
  const next = [
    visible,
    input.offsetLeft + bl,
    input.offsetTop + bt,
    input.offsetWidth - bl - br,
    input.offsetHeight - bt - bb,
    Math.max(0, px(cs.borderTopLeftRadius) - bl),
    bg,
    cs.paddingLeft,
    cs.fontFamily,
    cs.fontSize,
    cs.color,
    shown,
    input.disabled,
  ].join("|");
  if (next === rec.last) return;
  rec.last = next;

  const s = rec.face.style;
  s.display = visible ? "" : "none";
  if (!visible) return;

  s.left = `${input.offsetLeft + bl}px`;
  s.top = `${input.offsetTop + bt}px`;
  s.width = `${Math.max(0, input.offsetWidth - bl - br)}px`;
  s.height = `${Math.max(0, input.offsetHeight - bt - bb)}px`;
  s.borderRadius = `${Math.max(0, px(cs.borderTopLeftRadius) - bl)}px`;
  /* Opaque, or the platform's text and chevron would show through. A field
     with no background of its own gets white, which every date field here is. */
  s.backgroundColor = bg && bg !== "transparent" && bg !== "rgba(0, 0, 0, 0)" ? bg : "#fff";
  s.paddingLeft = cs.paddingLeft;
  s.fontFamily = cs.fontFamily;
  s.fontSize = cs.fontSize;

  rec.text.textContent = shown || EMPTY_TEXT;
  /* A picked date takes the field's own ink (grey when the field is disabled);
     the placeholder's grey comes from mobile.scss. */
  rec.text.style.color = shown ? cs.color : "";
  rec.face.classList.toggle("is-empty", !shown);
};

const enhance = (input) => {
  const host = input.parentNode;
  if (!host || records.has(input)) return;

  if (window.getComputedStyle(host).position === "static") {
    if (!repositioned.has(host)) repositioned.set(host, host.style.position);
    host.style.position = "relative";
  }

  const face = document.createElement("span");
  face.className = FACE;
  face.setAttribute("aria-hidden", "true");
  const text = document.createElement("span");
  text.className = `${FACE}-text`;
  const icon = document.createElement("span");
  icon.className = `${FACE}-icon`;
  icon.innerHTML = CALENDAR_SVG;
  face.appendChild(text);
  face.appendChild(icon);
  host.appendChild(face);

  const rec = { face, text, host, ro: null, last: "" };
  if (typeof window.ResizeObserver === "function") {
    rec.ro = new window.ResizeObserver(() => schedule());
    rec.ro.observe(host);
    rec.ro.observe(input);
  }
  records.set(input, rec);
  sync(input, rec);
};

const scan = () => {
  frame = 0;
  if (!running) return;
  for (const input of Array.from(records.keys())) {
    if (!input.isConnected) drop(input);
  }
  document.querySelectorAll(SELECTOR).forEach((input) => {
    const rec = records.get(input);
    if (rec) sync(input, rec);
    else enhance(input);
  });
};

function schedule() {
  if (!running || frame) return;
  frame = window.requestAnimationFrame(scan);
}

const onBodyMutation = (mutations) => {
  for (const m of mutations) {
    if (!isFace(m.target)) {
      schedule();
      return;
    }
  }
};

const onValueEvent = (e) => {
  const input = e.target;
  if (!input || input.tagName !== "INPUT" || input.type !== "date") return;
  const rec = records.get(input);
  if (rec) sync(input, rec);
  else schedule();
};

export const startDateLayer = () => {
  if (running || !MOBILE.dateLayer || typeof document === "undefined") return;
  running = true;
  bodyObserver = new window.MutationObserver(onBodyMutation);
  bodyObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["value", "disabled", "class"],
  });
  document.addEventListener("input", onValueEvent, true);
  document.addEventListener("change", onValueEvent, true);
  window.addEventListener("resize", schedule);
  scan();
};

export const stopDateLayer = () => {
  if (!running) return;
  running = false;
  if (frame) window.cancelAnimationFrame(frame);
  frame = 0;
  if (bodyObserver) bodyObserver.disconnect();
  bodyObserver = null;
  document.removeEventListener("input", onValueEvent, true);
  document.removeEventListener("change", onValueEvent, true);
  window.removeEventListener("resize", schedule);
  for (const input of Array.from(records.keys())) drop(input);
};
